<template>
    <div class="h-full w-full border-gray-200 relative">
        <div class="p-1.5 px-3 bg-white flex flex-col justify-center items-center z-20 border-b-2 rounded-t-md border-gray-200 sticky w-full">
            <QuickSearch
                class="w-full"
                placeholder="Search"
                @quick-search="quickSearch"
                @clear-quick-search="clearQuickSearch"
            />
        </div>
        <div class="h-[calc(100%-3rem)] overflow-y-auto top-0 relative">
            <TransitionGroup
                tag="ul"
                name="rows"
                v-if="Object.keys(visibleRows).length > 0"
            >
                <li
                    v-for="(row, key, index) in visibleRows"
                    :key="key"
                    :data-index="index"
                    class="even:white odd:bg-gray-100 hover:bg-indigo-100 focus:bg-indigo-200 active:bg-indigo-200 border-b px-3 py-1.5"
                    @dblclick="setRowOptions(key, row.options)"
                >
                    <ListRow
                        :key="key"
                        :active="isActive(row)"
                        :index="index"
                        :name="row.name"
                        :can-copy="row.copy"
                        :starting-index="0"
                        :ending-index="Object.keys(rows).length - 1"
                    >
                        <template #label>
                            <div class="flex flex-col w-full space-y-2 pb-2">
                                <div class="flex flex-col w-full font-semibold">
                                    <!-- <div>Syntax:</div> -->
                                    <div>{{ row.name }}</div>
                                </div>
                                <div class="flex flex-col w-full pl-4">
                                    <!-- <div>Definition:</div> -->
                                    <div>{{ row.label }}</div>
                                </div>
                            </div>
      
                        </template>
                    </ListRow>
                </li>
                <!-- <li class="h-80" :key="-1"></li> -->
            </TransitionGroup>
            <div v-else class="flex w-full justify-center items-center text-center font-normal text-gray-700  pt-4">
                No Matching Rows
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, reactive, watch } from 'vue'
import QuickSearch from './QuickSearch.vue'
import ListRow from './ListRow.vue'

const props = defineProps(['rows', 'defaultDisplay'])
const emit = defineEmits([
    'change-rows',
    'change-ordering',
    'change-active-rows',
    'set-row-options',
    'hide-active-rows',
    'clear-select-all',
    'refetch-results',
])

const quickSearchFilter = ref(null)
const rows = reactive(props.rows)
const visibleRows = reactive({})

const activeRows = computed(() => {
    let activeRowArr = []
    for(const row in rows) {
        if(Array.isArray(rows[row].value) && rows[row].value.length > 0){
            activeRowArr.push({row: row, name: rows[row].label, value: rows[row].options.row(option => rows[row].value.includes(option.value)).map(option => option.label)})
        } else if (!Array.isArray(rows[row].value) && rows[row].value && rows[row].options) {
            activeRowArr.push({row: row, name: rows[row].label, value: rows[row].options.row(option => rows[row].value == (option.value)).map(option => option.label)[0]})
        } else if (!Array.isArray(rows[row].value) && rows[row].value) {
            activeRowArr.push({row: row, name: rows[row].label, value: rows[row].value})
        } else {
            if(rows[row].min && rows[row].min.value) {
                activeRowArr.push({row: `${row}.min`, name: rows[row].min.label, value: rows[row].min.value})
            }
            if(rows[row].max && rows[row].max.value) {
                activeRowArr.push({row: `${row}.max`, name: rows[row].max.label, value: rows[row].max.value})
            }
        }
    }
    return activeRowArr
})

const activeRowsString = computed(() => {
    return activeRows.value ? activeRows.value.map(row => row.value).toString() : ''
})

const rowValues = computed(() => {
    let values = []
    for(const row in rows) {
        if(rows[row].min || rows[row].max) {
            if(rows[row].min) {
                values.push(rows[row].min.value)
            }
            if(rows[row].max) {
                values.push(rows[row].max.value)
            }
        } else {
        values.push(rows[row].value)
        }
    }
    return values
})

const rowValuesString = computed(() => {
    return rowValues.value ? rowValues.value.toString() : '';
})

watch(rowValuesString, () => {
    emitRowsChanged()
    emitClearSelectAll()
})

watch(activeRowsString, () => {
    emitActiveRowsChanged()
})

watch(() => quickSearchFilter.value, () => {
    setVisibleRows()
})

watch(() => props.reorderRows, () => {
    setVisibleRows(0)
})

function clearVisibleRows() {
    for (let visibleRow in visibleRows) delete visibleRows[visibleRow];
}

function setVisibleRows() {
    clearVisibleRows()
    if(quickSearchFilter.value && quickSearchFilter.value != '') {
        Object.entries(rows).sort(([, a], [, b]) => { return a.label.localeCompare(b.label)}).forEach(([rowKey, rowValue]) => {
            if(rowValue.visible) {
                if(rowKey.toLowerCase().includes(quickSearchFilter.value) || rowValue.name.toLowerCase().includes(quickSearchFilter.value) || rowValue.label.toLowerCase().includes(quickSearchFilter.value)
                ) {
                    visibleRows[rowKey] = rowValue
                } else {
                    if(!rowValue.pinned) {
                        delete visibleRows[rowKey]
                    }
                }
            } else {
                delete visibleRows[rowKey]
            }
        })
    } else {
        Object.entries(rows).sort(([, a], [, b]) => { return a.label.localeCompare(b.label)}).forEach(([rowKey, rowValue]) => {
            if(rowValue.visible) {
                visibleRows[rowKey] = rowValue
            } else {
                if(!rowValue.pinned) {
                    delete visibleRows[rowKey]
                }
            }
        })
    }
}

function emitRowsChanged(){
    emit("change-rows", rows)
}

function emitClearSelectAll(){
    emit('clear-select-all')
}

function emitActiveRowsChanged(){
    emit("change-active-rows", activeRows.value)
}

function setRowOptions(rowName, rowOptions) {
    if(rowOptions) {
        emit('set-row-options', rowName)
    }
}

function setRowExpression(row, expressionOption) {
    row.expression.value = expressionOption
    emitRowsChanged()
    emitClearSelectAll()
    emit('refetch-results')
}

function setRow(rowName, target) {
    if(target.includes("clear")){
        rows[rowName].value = []
    }
}

function setRowDefaults() {
    for(const row in rows){
        rows[row].value = rows[row].defaultValue
    }
}

function isActive(row) {
    if(Array.isArray(row.value) && row.value.length > 0){
        return true
    } else if(!Array.isArray(row.value) && row.value){
        return true
    } else if(row.min && row.min.value) {
        return true
    } else if(row.max && row.max.value) {
        return true
    }
    return false
}

function quickSearch(rowValue) {
    if(rowValue) {
        quickSearchFilter.value = rowValue.toLowerCase()
    } else {
        quickSearchFilter.value = null
    }
}

function clearQuickSearch() {
    quickSearchFilter.value = null
}

setRowDefaults()
setVisibleRows()
</script>

<style scoped>
.rows-move,
.rows-enter-active {
    transition: all 0.3s ease;
}
.rows-leave-active {
  transition: all 0.3s ease;
}
.rows-enter-from,
.rows-leave-to {
  opacity: 0;
  /* transform: translateX(-30px); */
}

.rows-leave-active {
    position: absolute;
    /* position: relative; */
}
</style>